import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {
    CommandLineInterface,
    consoleLogger,
    CoreDI,
    ICurrencyFormatter,
    inject,
    LanguageService,
    ReasonID
} from '@gongxigames/framework/core';
import {AutoplayOptions, AutoplayOptionsOptionsKeys, Message} from '@gongxigames/framework/slot/types';
import {WinPresentationUtils} from '@gongxigames/framework/slot/services/WinPresentationUtils';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic';
import {
    StoppedDueToInsufficientFunds,
    StoppedDueToLossLimitReached, StoppedDueToSingleWinReached, StoppedDueToWinLimitReached
} from '@gongxigames/framework/slot/configs/reasonAutoplayStop';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {AutoplayView} from '@gongxigames/framework/slot/modules/autoplay/AutoplayView';
import {AutoplayReadyForSpinState} from '@gongxigames/framework/slot/modules/autoplay/states/AutoplayReadyForSpinState';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay/AutoplayEvents';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {GenericPopupEvents} from '@gongxigames/framework/slot/modules/genericPopup';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {RealityCheckEvents} from '@gongxigames/framework/slot/modules/realitycheck';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {AutoplayViewMobile} from '@gongxigames/framework/slot/modules/autoplay/AutoplayViewMobile';
import {AutoplayViewDesktop} from '@gongxigames/framework/slot/modules/autoplay/AutoplayViewDesktop';
import {AutoplayBasicState} from '@gongxigames/framework/slot/modules/autoplay/states/AutoplayBasicState';
import {AutoplayWaitingForReadinessState} from './states/AutoplayWaitingForReadinessState';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';

export class AutoplayModule extends Module<AutoplayView, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('AutoplayModule');

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly READY_FOR_AUTOPLAY_STATE: string = 'readyforspin';
    public static readonly WAITING_FOR_READY_STATE: string = 'waintingforreadyness';

    @inject(CoreDI.CurrencyFormatter)
    protected readonly formatter!: ICurrencyFormatter;

    @inject(DI.WinPresentationUtils)
    private _winPresentationUtils!: WinPresentationUtils;

    @inject(CoreDI.LanguageService)
    private _languageService!: LanguageService;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    private _isAutoplayEnabled = false;
    private _isAutoplayJustStarted = false;

    private _disabledStopAutoplayButtonBy: (symbol | string)[] = [];

    private _isDelayBeforeSpin = false;
    private _roundDelay = 0;
    private _autoplaySpins = 0;
    private _winLimit: number | null = null;
    private _lossLimit: number | null = null;
    private _singleWinLimit: number | null = null;
    private _playerBalanceBeforeStartAutoplay = 0;

    public init(): void {
        super.init();

        const roundDelay = this.settings.get('roundDelay');

        if (roundDelay > 0) {
            this.roundDelay = roundDelay - AutoplayReadyForSpinState.TIME_BEFORE_NEXT_SPIN;
        }

        /* develblock:start */
        this._cli.addCommand('startAutoplay', (args: { spins: any; singleWin?: any; winLimit?: any; lossLimit?: any; }) => {
            let autoplaySpins = this.getAutoPlayValues()[0];
            const {spins = null, singleWin = null, winLimit = null, lossLimit = null} = args;

            if (args) {
                if (spins && this.getAutoPlayValues().indexOf(args.spins) >= 0) {
                    autoplaySpins = spins;
                }
            }

            this.startAutoplay({singleWin, winLimit, lossLimit, autoplaySpins, turboMode: false});
        });

        this._cli.addCommand('getAutoplayAmounts', () => consoleLogger.log(this.getAutoPlayValues()));
        /* develblock:end */
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on('General.UI.SPACEBAR_UP', this.onStopAutoplayButtonClicked);
        this.on('Autoplay.FORCE_STOP', this.onStopAutoplayButtonClicked);
        this.on(AutoplayEvents.FORCE_STOP_AUTOPLAY, this.onStopAutoplayButtonClicked);
        this.on(SpinButtonEvents.SPIN_BUTTON_HOLD, this.showOptionsPopup);
        this.on(KeypadControlsEvents.AUTOPLAY_CLICKED, this.showOptionsPopup);

        this.on('Autoplay.StopButton.ENABLE', this.enableStopAutoplayButton);
        this.on('Autoplay.StopButton.DISABLE', this.disableStopAutoplayButton);

        this.onCurrentStateHandle(WinPresentationEvents.INITIAL_ANIMATION_FINISHED);
        this.onCurrentStateHandle(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED);
        this.onCurrentStateHandle('General.WinPresentation.WIN_METER_FINISHED');
        this.onCurrentStateHandle(FlowModuleEvents.NO_WIN);
        this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);
        this.onCurrentStateHandle(FlowModuleEvents.WON_BIG_WIN);
        this.onCurrentStateHandle(BigWinEvents.FINISHED);
        this.onCurrentStateHandle(GenericPopupEvents.INTRO_STARTED);
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(RealityCheckEvents.SHOWED);
        this.onCurrentStateHandle(LogicEvents.REALITY_CHECK);
        this.onCurrentStateHandle(PickBonusEvents.STARTED);
        this.onCurrentStateHandle('Autoplay.START');
        this.onCurrentStateHandle('Autoplay.STOP');

        this.onViews(AutoplayView.STOP_AUTOPLAY_BUTTON_CLICKED, this.onStopAutoplayButtonClicked);
    }

    /**
     * listen to mobile and landscape events
     * @param id
     * @param listener
     */
    protected onViews(id: string | symbol, listener: Function): void {
        if (this.view instanceof AutoplayViewMobile) {
            this.view.views.forEach(view => {
                view.on(id, listener.bind(this, id), this);
            });
        } else {
            this.onView(id, listener as (id: any, ...args: any[]) => void);
        }
    }

    protected setupViews(): void {
        if (this.isPlatformDesktop()) {
            this.setView(AutoplayViewDesktop.NAME);
        } else {
            this.setView(AutoplayViewMobile.NAME);
        }
    }

    protected setupStates(): void {
        this.addState(new AutoplayBasicState(this, AutoplayModule.BASIC_STATE));
        this.addState(new AutoplayReadyForSpinState(this, AutoplayModule.READY_FOR_AUTOPLAY_STATE));
        this.addState(new AutoplayWaitingForReadinessState(this, AutoplayModule.WAITING_FOR_READY_STATE));

        this.gotoState(AutoplayModule.BASIC_STATE);
    }

    public get languageService(): LanguageService {
        return this._languageService;
    }

    public get roundDelay(): number {
        return this._roundDelay;
    }

    public set roundDelay(value: number) {
        this._roundDelay = value;
    }

    public get isAutoplayEnabled(): boolean {
        return this._isAutoplayEnabled;
    }

    public set isAutoplayEnabled(value: boolean) {
        this._isAutoplayEnabled = value;
    }

    public get isAutoplayJustStarted(): boolean {
        return this._isAutoplayJustStarted;
    }

    public set isAutoplayJustStarted(value: boolean) {
        this._isAutoplayJustStarted = value;
    }

    public get winPresentationUtils(): WinPresentationUtils {
        return this._winPresentationUtils;
    }

    public get isDelayBeforeSpin(): boolean {
        return this._isDelayBeforeSpin;
    }

    public set isDelayBeforeSpin(value: boolean) {
        this._isDelayBeforeSpin = value;
    }

    public startAutoplay({winLimit, singleWin, lossLimit, autoplaySpins, turboMode}: AutoplayOptions): void {
        if (this.isAutoplayEnabled) {
            throw new Error('Something went wrong in AutoplayBasicState. Can\'t be here while autoplay is enabled');
        }

        this._autoplaySpins = autoplaySpins ?? 0;
        this._winLimit = winLimit;
        this._lossLimit = lossLimit;
        this._singleWinLimit = singleWin;
        this._playerBalanceBeforeStartAutoplay = this._logic.getBalance();

        this.settings.set('quickSpin', turboMode !== null ? turboMode : false);
        this.settings.set('autoplayMode', true);

        this.isAutoplayEnabled = true;
        this.eventManager.emit(AutoplayEvents.START_AUTOPLAY);
        this.isAutoplayJustStarted = true;
        this.gotoState(AutoplayModule.READY_FOR_AUTOPLAY_STATE);
        this.eventManager.emit('UI.Buttons.TurboMode', this.settings.get('quickSpin'));
        this.eventManager.emit('Autoplay.STARTED');
    }

    public stopAutoplay(): void {
        if (this.isAutoplayEnabled) {
            this._autoplaySpins = 0;
            this.isAutoplayEnabled = false;
            this.settings.set('autoplayMode', false);
            this.eventManager.emit(AutoplayEvents.STOP_AUTOPLAY);
            this.eventManager.emit('Autoplay.STOPPED');
        }
        this.view.hideAutoplayUI();
    }

    public shouldAutoplayStopAndShowMessage(): boolean {
        if (!this.logic.canSpin(this.logic.getTotalBet())) {
            this.showStopReasonMessage(StoppedDueToInsufficientFunds);

            return true;
        }

        if (this.isLossLimit()) {
            this.showStopReasonMessage(StoppedDueToLossLimitReached);

            return true;
        }

        if (this.isWinLimit()) {
            this.showStopReasonMessage(StoppedDueToWinLimitReached);

            return true;
        }

        if (this._autoplaySpins === 0) {
            return true;
        }

        if (this.isSingleWinLimit()) {
            this.showStopReasonMessage(StoppedDueToSingleWinReached);

            return true;
        }

        return false;
    }

    public setSpins(value: number): void {
        this._autoplaySpins = value;
    }

    public decreaseSpins(): void {
        this._autoplaySpins -= 1;
    }

    public updateSpinsView(): void {
        this.view.setAutoplayFieldText(String(this._autoplaySpins));
    }

    public gotoBasicStateBeforeBonusGame(): void {
        if (this.settings.get('autoplayStopWhenAnyBonus')) {
            this.setSpins(0);
        }

        this.gotoState(AutoplayModule.BASIC_STATE);
    }

    protected canShowOptionsPopup(): boolean {
        return true;
    }

    private showStopReasonMessage(message: Message): void {
        this.eventManager.emit('General.Message.SHOW', message);
    }

    private onStopAutoplayButtonClicked(): void {
        this.onCurrentStateHandleEvent(AutoplayView.STOP_AUTOPLAY_BUTTON_CLICKED);
        this.view.stopAutoplayButtonClickAction();
    }

    private isSingleWinLimit(): boolean {
        const winLimit = this._singleWinLimit;

        return winLimit !== null && winLimit > 0 && this.logic.spinData && this.logic.getWin() >= winLimit * this.formatter.fractionSize;
    }

    private isWinLimit(): boolean {
        const winLimit = this._winLimit;
        const autoplayBalance = this._playerBalanceBeforeStartAutoplay;

        return winLimit != null && this.logic.getBalance() >= (winLimit * this.formatter.fractionSize) + autoplayBalance;
    }

    private isLossLimit(): boolean {
        const lossLimit = this._lossLimit;
        const autoplayBalance = this._playerBalanceBeforeStartAutoplay;

        return lossLimit != null && autoplayBalance - this.logic.getBalance() >= (lossLimit * this.formatter.fractionSize);
    }

    private getAutoPlayValues(): number[] {
        return this.settings.get('autoplayValues');
    }

    private showOptionsPopup(): void {
        if (!this.canShowOptionsPopup()) {
            return;
        }

        const autoplayValues = this.getAutoPlayValues();
        const requiredOptions: AutoplayOptionsOptionsKeys[] = ['autoplaySpins'];

        if (this.settings.get('autoplayRequiredLossLimit')) {
            requiredOptions.push('lossLimit');
        }

        if (this.settings.get('autoplayRequiredSingleWin')) {
            requiredOptions.push('singleWin');
        }

        if (this.settings.get('autoplayRequiredWinLimit')) {
            requiredOptions.push('winLimit');
        }

        let defaultSpins = autoplayValues[0];

        if (this.settings.get('autoplayDefaultSelectedSpins') !== undefined) {
            defaultSpins = autoplayValues[this.settings.get('autoplayDefaultSelectedSpins')];
        }

        this.eventManager.emit('Autoplay.SHOW_OPTIONS_POPUP', {
            autoplayValues,
            requiredOptions,
            defaultOptions: {autoplaySpins: defaultSpins, turboMode: this.settings.get('quickSpin')},
            disabledTurboButton: this._settings.get('turboModeDisabled'),
            disabledLimits: this._settings.get('allAutoplayLimitsDisabled'),
            autoplayShowBetAndWager: this._settings.get('autoplayShowBetAndWager'),
            autoplayShowConfirmationPopup: this._settings.get('autoplayShowConfirmationPopup')
        });
    }

    public enableStopAutoplayButton(_eventID: string | symbol, reason: ReasonID): void {
        const index = this._disabledStopAutoplayButtonBy.indexOf(reason);

        if (index !== -1) {
            this._disabledStopAutoplayButtonBy.splice(index, 1);

            if (!this._disabledStopAutoplayButtonBy.length) {
                this.view.enableStopAutoplayButton();
            }
        }
    }

    public disableStopAutoplayButton(_eventID: string | symbol, reason: ReasonID): void {
        if (this._disabledStopAutoplayButtonBy.indexOf(reason) === -1) {
            this._disabledStopAutoplayButtonBy.push(reason);
            this.view.disableStopAutoplayButton();
        }
    }
}
